import React, { useEffect, useState } from "react";
import {
  View,
  Text,
  Modal,
  TouchableOpacity,
  StyleSheet,
  Image,
} from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { AppImages } from "../utility/AppImages";
import { AppColors } from "../utility/AppColors";
import { AppFontStyle } from "../styles/AppFontStyle";
import {
  StackActions,
  useNavigation,
  useNavigationState,
} from "@react-navigation/native";
import { navigateToScreen } from "../utility/NavigationHelper";
import { AppScreens } from "../screens/Utility/AppScreens";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { toast } from "react-toastify";
import { getLoginData } from "../utility/AppStoreData";
import { clearAllCaches } from "../utility/cacheManager";
import { logoutUser } from "../services/authAPI";
import { CropImage } from "./choose-avatar-components/CropImage";
import { getUserProfile } from "../services/authAPI";
import SidebarBottomWalkthrough from "./walk-through-common-components/homeScreenTooltips/SidebarBottomWalkthrough";
import ProfileWalkthrough from "./walk-through-common-components/homeScreenTooltips/ProfileWalkthrough";
import Avatar from "./choose-avatar-components/Avatar";

const MoreTab = ({ isVisible, onClose, toggleMoreModal = () => { } }) => {
  const navigation = useNavigation();
  const [accessToken, setAccessToken] = React.useState("");
  const [userProfileData, setUserProfileData] = useState(null);

  // Get current route name to check which screen is active
  const currentRouteName = useNavigationState((state) => {
    if (!state) return null;

    // Recursively find the deepest route name
    const getActiveRouteName = (navigationState) => {
      // Safety checks to prevent crashes
      if (
        !navigationState ||
        !navigationState.routes ||
        navigationState.routes.length === 0
      ) {
        return null;
      }

      const route = navigationState.routes[navigationState.index];

      // Safety check: ensure route exists
      if (!route) {
        console.warn(
          "MoreTab: Route is undefined at index",
          navigationState.index
        );
        return null;
      }

      // If this route has nested state, go deeper
      if (route.state) {
        return getActiveRouteName(route.state);
      }

      // Return the route name at the deepest level
      return route.name;
    };

    return getActiveRouteName(state);
  });

  async function getLoginUserData() {
    const data = await getLoginData();

    if (data != null) {
      setAccessToken(data.AccessToken);
      userProfileAPI(data.AccessToken);
    }
  }

  useEffect(() => {
    getLoginUserData();
  }, []);

  const userProfileAPI = async (AccessToken?: string) => {
    try {
      const data = await getUserProfile(AccessToken);
      const responseBody =
        typeof data.body === "string" ? JSON.parse(data.body) : data.body;

      if (data.statusCode === 200) {
        setUserProfileData(responseBody);
      } else if (data.statusCode == 400) {
        toast.error("Bad Request");
      } else if (data.statusCode > 400 && data.statusCode < 420) {
        toast.error("Unauthorized");
      } else if (data.statusCode >= 500) {
        toast.error("Internal Server Error");
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const logout = async () => {
    try {
      const params = { accessToken: accessToken };

      // Call logout API without waiting (fire and forget)
      logoutUser(params)
        .then((response) => {
          if (response.statusCode === 200) {
            console.log("Logout API success:", response.body?.message);
          } else {
            console.warn("Logout API warning:", response);
          }
        })
        .catch((apiError) => {
          console.error("Logout API error:", apiError);
        });

      // Always clear all caches - user should be logged out locally regardless of API response
      await clearAllCaches();

      // Set a flag in sessionStorage to show toast after redirect
      sessionStorage.setItem("showLogoutSuccessToast", "true");

      // Force a full page reload to ensure all React state is cleared
      // This will restart the app and AuthContext will handle navigation
      window.location.href = window.location.origin + "/login";
    } catch (error) {
      console.error("Logout error:", error);
      // Set flag for error message
      sessionStorage.setItem("showLogoutErrorToast", "true");
      // Even if there's an error, force a reload to clear all state
      window.location.href = window.location.origin + "/login";
    }
  };

  useEffect(() => {
    async function loadProfileData() {
      try {
        const storedProfilePicture = await AsyncStorage.getItem(
          "profilePicture"
        );
        const storedUserID = await AsyncStorage.getItem("userID");
        if (storedUserID || storedProfilePicture) {
          setUserProfileData((prevData) => ({
            ...prevData,
            profilePicture: storedProfilePicture || prevData.profilePicture,
            userID: storedUserID || prevData.userID,
          }));
        }
      } catch (error) {
        console.error("Error retrieving data from AsyncStorage:", error);
      }
    }

    loadProfileData();
  }, [isVisible]);

  // Helper function to render menu items with highlighting
  const renderMenuItem = (screenName, icon, title, onPress) => {
    const isActive = currentRouteName === screenName;

    const content = (
      <View style={styles.menuItem}>
        <Image
          source={icon}
          style={{
            width: 24,
            height: 24,
          }}
        />
        <Text style={AppFontStyle.BOLD_ANYBODY_16}>{title}</Text>
      </View>
    );

    if (isActive) {
      return (
        <TouchableOpacity onPress={onPress} activeOpacity={0.8}>
          <LinearGradient
            colors={[
              AppColors.buttonRed_DC2626,
              AppColors.buttonRedDark_991B1B,
            ]}
            start={[0, 0]}
            end={[1, 0]}
          >
            {content}
          </LinearGradient>
        </TouchableOpacity>
      );
    }

    return (
      <TouchableOpacity
        style={{ backgroundColor: AppColors.purple_453B50 }}
        onPress={onPress}
        activeOpacity={0.8}
      >
        {content}
      </TouchableOpacity>
    );
  };

  return (
    <Modal transparent={true} visible={isVisible} onRequestClose={onClose}>
      <View style={styles.modalOverlay}>
        <TouchableOpacity style={styles.overlayBackground} onPress={onClose} />
        <View style={styles.modalContent}>
          <ProfileWalkthrough
            toggleMoreModal={toggleMoreModal}
            content={
              <TouchableOpacity
                style={styles.profileHeader}
                onPress={() => {
                  onClose();
                  navigateToScreen(navigation, AppScreens.ProfileMain);
                }}
              >
                <View
                  style={{
                    borderRadius: 28,
                    backgroundColor: AppColors.white,
                    overflow: "hidden",
                  }}
                >
                  <Avatar
                    src={userProfileData?.profilePicture}
                    variant="profileSmall"
                  />
                </View>
                {/* <Text style={styles.username}> {userProfileData?.userID}</Text> */}
                <Text style={styles.username}>
                  {userProfileData?.userID.split("").length > 8
                    ? userProfileData?.userID.split("").slice(0, 8).join("") +
                    "..."
                    : userProfileData?.userID}
                </Text>
              </TouchableOpacity>
            }
          />

          <SidebarBottomWalkthrough
            toggleMoreModal={toggleMoreModal}
            content={
              <>
                {renderMenuItem(
                  AppScreens.HelpScreen,
                  AppImages.icHelp,
                  "Help",
                  () => {
                    onClose();
                    navigateToScreen(navigation, AppScreens.HelpTabStack);
                  }
                )}

                {renderMenuItem(
                  AppScreens.SettingWebScreen,
                  AppImages.icSetting,
                  "Settings",
                  () => {
                    onClose();
                    navigateToScreen(navigation, AppScreens.SettingMain);
                  }
                )}

                <TouchableOpacity style={styles.menuItem} onPress={logout}>
                  {" "}
                  <Image
                    source={AppImages.icNotificationBing}
                    style={{
                      width: 24,
                      height: 24,
                    }}
                  />
                  <Text style={AppFontStyle.BOLD_ANYBODY_14}>Sign Out</Text>
                </TouchableOpacity>
              </>
            }
          />
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "flex-end",
    alignItems: "flex-end",
  },
  overlayBackground: {
    position: "absolute",
    width: "100%",
    height: "100%",
  },
  modalContent: {
    backgroundColor: AppColors.purple_453B50,
    // paddingVertical: 10,
    width: 170,
    bottom: 60,
    margin: 10,
    elevation: 5,
  },
  profileHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    paddingHorizontal: 10,
    paddingVertical: 15,
    minWidth: 170,
  },
  menuItem: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 15,
    gap: 10,
    minWidth: 151,
    backgroundColor: AppColors.purple_453B50,
    paddingHorizontal: 10,
  },
  username: {
    ...AppFontStyle.BOLD_ANYBODY_16,
  },
});

export default MoreTab;
